<template>
  <div class="k-list-skeleton">
    <div v-for="row in rows" :key="row" class="k-list-skeleton__row">
      <span class="k-list-skeleton__bone k-list-skeleton__bone--avatar"></span>
      <div class="k-list-skeleton__lines">
        <span
          class="k-list-skeleton__bone"
          :style="{ width: titleWidth(row) }"
        ></span>
        <span
          class="k-list-skeleton__bone k-list-skeleton__bone--sub"
          :style="{ width: subWidth(row) }"
        ></span>
      </div>
      <span class="k-list-skeleton__bone k-list-skeleton__bone--action"></span>
    </div>
  </div>
</template>

<script>
// Row skeleton for the two-line `.list-card` lists (dashboard cards), the list
// counterpart of k-table-skeleton. Geometry mirrors `.list-card-btn`: a rounded
// 48px avatar, a title + sub-info line, and the trailing action button — so the
// rows don't shift when the real data lands.
//
// Bind it with v-if="smoothLoading" (smoothLoading mixin) so a fast response
// still shows a full loading beat instead of a flicker.
const TITLE_WIDTHS = ["62%", "48%", "70%", "54%", "58%"];
const SUB_WIDTHS = ["34%", "42%", "28%", "38%", "32%"];

export default {
  props: {
    rows: {
      type: Number,
      default: 5,
    },
  },
  methods: {
    // Deterministic variation so rows don't look mechanically uniform.
    titleWidth(row) {
      return TITLE_WIDTHS[(row - 1) % TITLE_WIDTHS.length];
    },
    subWidth(row) {
      return SUB_WIDTHS[(row - 1) % SUB_WIDTHS.length];
    },
  },
};
</script>

<style scoped>
.k-list-skeleton {
  /* Gentle entrance. The minimum-display floor (smoothLoading mixin) keeps the
     skeleton on screen long enough for this fade + the sweep below to actually
     be seen, so a fast load reads as a calm beat rather than a flash. */
  animation: k-list-skeleton-reveal 0.35s ease;
}

.k-list-skeleton__row {
  display: flex;
  align-items: center;
  gap: 16px;
  /* Same box as .list-card-btn, so the swap to real rows is seamless. */
  padding: var(--spacing-2, 0.5rem);
  margin-bottom: 8px;
  border: 1px solid var(--neutral-100, #f5f5f5);
  border-radius: var(--Radius--small, 0.625rem);
  background: #fcfcfc;
}

.k-list-skeleton__row:last-of-type {
  margin-bottom: 0px;
}

.k-list-skeleton__lines {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 8px;
  overflow: hidden;
}

.k-list-skeleton__bone {
  position: relative;
  display: inline-block;
  height: 14px;
  border-radius: 7px;
  /* Tinted with the app's primary token so it stays in sync with the theme. */
  background: color-mix(in srgb, var(--primary) 22%, transparent);
  overflow: hidden;
}

.k-list-skeleton__bone--avatar {
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  border-radius: 4px;
}

.k-list-skeleton__bone--sub {
  height: 10px;
  border-radius: 5px;
}

.k-list-skeleton__bone--action {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.k-list-skeleton__bone::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    transparent,
    color-mix(in srgb, var(--primary) 42%, transparent),
    transparent
  );
  /* Calm, unhurried sweep so it reads as ambient loading, not a twitch. */
  animation: k-list-skeleton-shimmer 1.6s ease-in-out infinite;
}

@keyframes k-list-skeleton-shimmer {
  100% {
    transform: translateX(100%);
  }
}

@keyframes k-list-skeleton-reveal {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Respect users who ask for less motion: reveal instantly, drop the shimmer. */
@media (prefers-reduced-motion: reduce) {
  .k-list-skeleton {
    animation-duration: 0.001s;
  }

  .k-list-skeleton__bone::after {
    animation: none;
  }
}
</style>
